import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import type { NotificationStreamItem } from "@situate/contracts";

import {
  LiveNotificationCard,
  getNotificationRowDomId,
} from "@/features/deployment-dashboard/widgets/panels/live-notifications-rail/live-notification-card";

import { RightRailContainer, SectionHeading, StatusBadge } from "@/shared/ui";
import type { StatusBadgeSeverity } from "@/shared/ui";
import { CloseIcon, NotificationsIcon } from "@/shared/ui/console-icons";

interface LiveNotificationsRailProps {
  notifications: readonly NotificationStreamItem[];

  selectedNotificationId?: string | null;
  onSelectNotification: (notificationId: string) => void;
  /** Shows a close ("X") action in the header that fully closes the rail, e.g. the shared
   * layout-level notification center rail. Omitted when the rail cannot be dismissed. */
  onClosePanel?: () => void;
  /** Forwarded to RightRailContainer; see its docs for details. */
  floating?: boolean;
}

interface NotificationGroup {
  notification: NotificationStreamItem;
  notifications: NotificationStreamItem[];
}

function isNewerNotification(
  candidate: NotificationStreamItem,
  current: NotificationStreamItem
): boolean {
  const candidateTime = new Date(candidate.created_at).getTime();
  const currentTime = new Date(current.created_at).getTime();
  return (
    candidateTime > currentTime ||
    (candidateTime === currentTime &&
      candidate.notification_id.localeCompare(current.notification_id) > 0)
  );
}

function groupNotifications(notifications: readonly NotificationStreamItem[]): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  const alertGroups = new Map<string, NotificationGroup>();

  for (const notification of notifications) {
    if (notification.type !== "alert") {
      groups.push({ notification, notifications: [notification] });
      continue;
    }
    const existingGroup = alertGroups.get(notification.source.id);
    if (existingGroup !== undefined) {
      if (
        !existingGroup.notifications.some(
          (item) => item.notification_id === notification.notification_id
        )
      ) {
        existingGroup.notifications.push(notification);
      }
      if (isNewerNotification(notification, existingGroup.notification)) {
        existingGroup.notification = notification;
      }
      continue;
    }
    const group = { notification, notifications: [notification] };
    alertGroups.set(notification.source.id, group);
    groups.push(group);
  }
  return groups;
}

function getUniqueNotificationCount(notifications: readonly NotificationStreamItem[]): number {
  return new Set(
    notifications.map((notification) => `${notification.source.type}:${notification.source.id}`)
  ).size;
}

function getHighestSeverityBadge(
  notifications: readonly NotificationStreamItem[]
): StatusBadgeSeverity {
  const rank = (severity: NotificationStreamItem["severity"]): number => {
    switch (severity) {
      case "critical":
        return 0;
      case "high":
        return 1;
      case "medium":
        return 2;
      case "warning":
        return 3;
      default:
        return 4;
    }
  };
  const highest = notifications.reduce<NotificationStreamItem["severity"] | null>(
    (current, item) =>
      current == null || rank(item.severity) < rank(current) ? item.severity : current,
    null
  );
  return highest ?? "info";
}

/**
 * Layout-level bell panel: a list-only view of the streamed notifications
 * notifications, newest first. Selecting a card only highlights it (via
 * `selectedNotificationId`); there is no detail rail or route.
 */
export function LiveNotificationsRail({
  notifications,
  selectedNotificationId = null,
  onSelectNotification,
  onClosePanel,
  floating,
}: LiveNotificationsRailProps) {
  const [expandedAlertIds, setExpandedAlertIds] = useState<ReadonlySet<string>>(() => new Set());
  const notificationGroups = useMemo(() => groupNotifications(notifications), [notifications]);

  useEffect(() => {
    if (selectedNotificationId == null) {
      return;
    }
    const node = document.getElementById(getNotificationRowDomId(selectedNotificationId));
    if (node == null) {
      return;
    }
    node.scrollIntoView({ block: "nearest" });
    node.focus();
  }, [selectedNotificationId]);

  return (
    <RightRailContainer
      ariaLabel="Active notifications"
      floating={floating}
      header={
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <SectionHeading
              icon={<NotificationsIcon sx={{ fontSize: "0.875rem" }} />}
              component="h2"
            >
              Notifications
            </SectionHeading>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <StatusBadge
                severity={getHighestSeverityBadge(notifications)}
                label={`${getUniqueNotificationCount(notifications)} Notifications`}
              />
              {onClosePanel != null && (
                <IconButton
                  aria-label="Close notifications"
                  size="small"
                  onClick={onClosePanel}
                  sx={{ color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Real-time notifications for this deployment.
          </Typography>
        </>
      }
    >
      {notifications.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 1 }}>
          No notifications.
        </Typography>
      ) : (
        <>
          {notificationGroups.map(({ notification, notifications: groupedItems }) => {
            const alertId = notification.type === "alert" ? notification.source.id : null;
            const canExpand = alertId !== null && groupedItems.length > 1;
            const isExpanded = alertId !== null && expandedAlertIds.has(alertId);

            if (!canExpand) {
              return (
                <LiveNotificationCard
                  key={notification.notification_id}
                  item={notification}
                  selected={notification.notification_id === selectedNotificationId}
                  onSelect={onSelectNotification}
                />
              );
            }

            return (
              <Box key={notification.notification_id}>
                <Box sx={{ position: "relative", pb: 1 }}>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 2,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      transform: "translateY(4px)",
                    }}
                  />
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <LiveNotificationCard
                      item={notification}
                      groupedCount={groupedItems.length}
                      selected={notification.notification_id === selectedNotificationId}
                      onSelect={onSelectNotification}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={
                      isExpanded ? "Hide grouped notifications" : "Show grouped notifications"
                    }
                    aria-expanded={isExpanded}
                    onClick={() => {
                      if (alertId === null) return;
                      setExpandedAlertIds((current) => {
                        const next = new Set(current);
                        if (next.has(alertId)) next.delete(alertId);
                        else next.add(alertId);
                        return next;
                      });
                    }}
                    sx={(theme) => ({
                      position: "absolute",
                      right: 10,
                      bottom: -2,
                      zIndex: 2,
                      p: 0.2,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": { backgroundColor: theme.palette.action.hover },
                    })}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={isExpanded} unmountOnExit>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      ml: 1.5,
                      pl: 1.5,
                      borderLeft: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    {groupedItems
                      .filter((item) => item.notification_id !== notification.notification_id)
                      .map((item) => (
                        <LiveNotificationCard
                          key={item.notification_id}
                          item={item}
                          selected={item.notification_id === selectedNotificationId}
                          onSelect={onSelectNotification}
                        />
                      ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </>
      )}
    </RightRailContainer>
  );
}
